import { useState, useEffect, useCallback } from "react";

import { DonutChart } from "@similarweb/pro-logic-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import { allTrackers } from "app/services/track/track";

import { ShareTypeSwitcher, SwitcherOption } from "../shared/ShareTypeSwitcher";
import { NA_VALUE } from "../utils/utils";

import { formatBreakdownConfig, switcherOptionToDataType } from "./configs/breakdownConfigs";
import { chartConfig } from "./configs/PerformanceAdPlacementChartConfig";
import type { IFormatShare } from "./hooks/useFormatBreakdown";
import { Legend } from "./Legend";
import { ShortEmptyState } from "./shortEmptyState";
import {
    BreakdownChartContainer,
    BreakdownChartWithLegendContainer,
    BreakdownLegendWrapper,
    BreakdownChartWrapper,
} from "./StyledComponents";
import { normalizePercentages } from "./utils/graphUtils";
import { getShareValueBySwitcherOption } from "./utils/utils";

interface IDisplayAdvertisingFormatBreakdownWidgetProps {
    spendShare: IFormatShare;
    impressionsShare: IFormatShare;
    visitsShare: IFormatShare;
    showEmptyState?: boolean;
    hasError?: boolean;
}

export const DisplayAdvertisingFormatBreakdownWidget = ({
    spendShare,
    impressionsShare,
    visitsShare,
    showEmptyState = false,
    hasError = false,
}: IDisplayAdvertisingFormatBreakdownWidgetProps) => {
    const hasData = useCallback((formatShare: IFormatShare): boolean => {
        return (formatShare?.video || 0) + (formatShare?.banner || 0) + (formatShare?.native || 0) > 0;
    }, []);

    const [selectedSwitcherOption, setSelectedSwitcherOption] = useState<SwitcherOption>(() => {
        const options = [SwitcherOption.Spend, SwitcherOption.Impressions, SwitcherOption.Visits];
        return (
            options.find((option) =>
                hasData(getShareValueBySwitcherOption(option, spendShare, impressionsShare, visitsShare)),
            ) || SwitcherOption.Spend
        );
    });

    const handleSwitcherChange = (option: SwitcherOption) => {
        setSelectedSwitcherOption(option);
        const dataType = switcherOptionToDataType[option];
        allTrackers.dispatchTrackEvent("Metric Button", "switch", dataType);
    };

    useEffect(() => {
        const isCurrentOptionValid = hasData(
            getShareValueBySwitcherOption(selectedSwitcherOption, spendShare, impressionsShare, visitsShare),
        );

        if (!isCurrentOptionValid) {
            const options = [SwitcherOption.Spend, SwitcherOption.Impressions, SwitcherOption.Visits];
            const firstAvailableOption = options.find((option) =>
                hasData(getShareValueBySwitcherOption(option, spendShare, impressionsShare, visitsShare)),
            );
            if (firstAvailableOption !== undefined) {
                setSelectedSwitcherOption(firstAvailableOption);
            }
        }
    }, [spendShare, impressionsShare, visitsShare, selectedSwitcherOption, hasData]);

    const formatShare = getShareValueBySwitcherOption(
        selectedSwitcherOption,
        spendShare,
        impressionsShare,
        visitsShare,
    );
    const rawValues = [formatShare?.video || 0, formatShare?.banner || 0, formatShare?.native || 0];
    const normalizedValues = normalizePercentages(rawValues);

    const formatBreakdownData = formatBreakdownConfig.map((config, index) => ({
        color: config.color,
        name: translate(config.translationKey),
        id: config.id,
        visible: true,
        y: normalizedValues[index],
        total: normalizedValues[index],
    }));

    const legendItems = formatBreakdownConfig.map((config, index) => ({
        name: translate(config.translationKey),
        value: normalizedValues[index] ? (percentageSignFilter()(normalizedValues[index], 0) as string) : NA_VALUE,
        color: config.color,
    }));

    return (
        <Flex
            flexDirection="column"
            height="100%"
        >
            <Flex
                padding="0 24px 16px 24px"
                justifyContent="flex-start"
            >
                <ShareTypeSwitcher
                    selectedSwitcherOption={selectedSwitcherOption}
                    setSelectedSwitcherOption={handleSwitcherChange}
                />
            </Flex>
            <BreakdownChartContainer>
                {showEmptyState ? (
                    <ShortEmptyState isError={!!hasError} />
                ) : (
                    <BreakdownChartWithLegendContainer>
                        <BreakdownChartWrapper>
                            <DonutChart
                                data={[{ data: formatBreakdownData }]}
                                config={chartConfig}
                                shouldPresentLegend={false}
                                innerSize="55%"
                            />
                        </BreakdownChartWrapper>
                        <BreakdownLegendWrapper>
                            <Legend
                                items={legendItems}
                                dataAutomation="format-breakdown-legend"
                            />
                        </BreakdownLegendWrapper>
                    </BreakdownChartWithLegendContainer>
                )}
            </BreakdownChartContainer>
        </Flex>
    );
};
